import React, { useEffect, useState } from 'react'
import { AppDataService } from '../services/AppDataService';
import useInfoModal from '../hooks/useInfoModal';
import { MODAL_TYPE_ENUM } from '../enums/CommonEnums';
import SkeletonGroup from '../components/SkeletonGroup';
import { UsersTable } from '../components/UsersTable';
import { Accordion, Badge, Box, Button, Center, Collapse, Divider, Group, Paper, Select, Text, Title } from '@mantine/core';
import { IconAlarm, IconBulb, IconEye, IconFileDownload, IconFileTypeXls, IconLogin, IconLogin2, IconRefresh, IconUsersGroup } from '@tabler/icons-react';
import { RecentRegistrationsTable } from '../components/RecentRegistrationsTable';
import OBRCDateRangePicker from '../components/OBRCDateRangePicker';
import { addDays, subDays } from 'date-fns';

const AdminViewUsers = () => {
  const [users, setUsers] = useState([]);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState([subDays(new Date(), 7), new Date()]);
  const openModal = useInfoModal();

  useEffect(() => {
    getUsers().catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    });

    fetchRecentContainerRegistrations(dates[0].toISOString(), dates[1].toISOString()).catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    });
  }, []);

  const getUsers = async () => {
    setLoading(true);

    const response = await AppDataService.AdminGetUsers();

    if(Array.isArray(response?.data?.data)) {
      setUsers(response.data?.data);
    }
    else {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, 'Failed to fetch users.');
    }

    setLoading(false);
  };

  const fetchRecentContainerRegistrations = async (startDt, endDt) => {
    setLoading(true);

    const response = await AppDataService.AdminGetRecentUserContainerRegistrations(startDt, endDt);

    if(Array.isArray(response?.data?.registrations)) {
      setRecentRegistrations(response.data.registrations);
    }
    else {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, 'Failed to fetch recent container registrations.');
    }

    setLoading(false);
  }

  const handleClickRefresh = (e) => {
    e.preventDefault();

    getUsers().catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    });
  }

  const handleClickHideNonOBRCMembers = (e) => {
    e.preventDefault();

    const obrcMemberUsers = users.filter(x => x.isOBRCMember === true);

    setUsers(obrcMemberUsers);
  }

  const onDateChange = (selectedDates) => {
    const isoStartDt = selectedDates[0].startDate.toISOString();
    const isoEndDt = selectedDates[0].endDate.toISOString();

    setDates([selectedDates[0].startDate, selectedDates[0].endDate]);

    fetchRecentContainerRegistrations(isoStartDt, isoEndDt).catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    })
  }

  const handleClickDownloadRecentRegistrations = async (e) => {
    e.preventDefault();

    setLoading(true);

    const response = await AppDataService.AdminDownloadRecentRegistrations(dates[0].toISOString(), dates[1].toISOString());

    if(!response.hasError()) {
      const isoStartDt = dates[0].toISOString();
      const isoEndDt = dates[1].toISOString();
  
      await fetchRecentContainerRegistrations(isoStartDt, isoEndDt).catch(err => {
        setLoading(false);
        openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
      })
    }
    else {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, response.errMsg);
    }
  }

  const handleClickDownloadUserList = async (e) => {
    e.preventDefault();

    const response = await AppDataService.DownloadUserList();
  }

  return (
    <>
      <Accordion variant='contained' defaultValue='user-accounts'>
        <Accordion.Item value='user-accounts'>
          <Accordion.Control icon={<IconUsersGroup />}><Text fw={500}>User Accounts</Text></Accordion.Control>
          <Accordion.Panel>
          {
            loading ? <SkeletonGroup height={600} width={1000} num={1} /> : 
            users.length > 0 ? (
              <>
                <Group mb='md'>
                  <Button loading={loading} leftIcon={<IconRefresh size={15} />} onClick={handleClickRefresh}>Refresh List</Button>
                  <Button loading={loading} leftIcon={<IconFileDownload size={15} />} onClick={handleClickDownloadUserList}>Download User List</Button>
                  <Button color='dark' leftIcon={<IconEye size={15} />} onClick={handleClickHideNonOBRCMembers}>Hide Non-OBRC Members</Button>
                </Group>
                <UsersTable tableData={users} /> 
              </>
            ) : (
              <Paper shadow='md' withBorder radius='lg' maw={800} mx='auto' mt={50} p={50}>
                <Center>
                  <IconBulb stroke={1.5} color='#228be6' size={100} />
                </Center>
                <Text fz={20} mt={25} ta='center'>No users found.</Text>
              </Paper>
            )
          }
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value='recent-registrations'>
          <Accordion.Control icon={<IconLogin />}><Text fw={500}>Recent Registrations</Text></Accordion.Control>
          <Accordion.Panel>
            <Group noWrap={true} align='flex-start' position='left'>
              <Box>
                <OBRCDateRangePicker onDateSelect={onDateChange} initialEndDate={dates[1]} initialStartDate={dates[0]} />
                <Button loading={loading} color='dark' fullWidth mt='md' leftIcon={<IconFileTypeXls size={15} />} onClick={handleClickDownloadRecentRegistrations}>Download Data</Button>
              </Box>
              {
                loading ? <SkeletonGroup height={800} width={1000} num={1} /> : recentRegistrations.length > 0 ? (
                  <RecentRegistrationsTable tableData={recentRegistrations} />
                ) : (
                  <Paper shadow='md' withBorder radius='lg' maw={800} mx='auto' mt={50} p={50}>
                    <Center>
                      <IconBulb stroke={1.5} color='#228be6' size={100} />
                    </Center>
                    <Text fz={20} mt={25} ta='center'>No recent submissions found. Try adjusting the max lookup days dropdown.</Text>
                  </Paper>
                )
              }
            </Group>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      
    </>
  )
}

export default AdminViewUsers;